import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';

interface MediaFormData {
  url: string;
  alt?: string;
  caption?: string;
  isPrimary: boolean;
}

interface MediaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string;
  media?: {
    id: string;
    url: string;
    alt: string | null;
    caption: string | null;
    isPrimary: boolean;
  };
  onSuccess?: () => void;
}

export function MediaModal({ open, onOpenChange, packageId, media, onSuccess }: MediaModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!media;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<MediaFormData>({
    defaultValues: media
      ? {
          url: media.url,
          alt: media.alt ?? undefined,
          caption: media.caption ?? undefined,
          isPrimary: media.isPrimary,
        }
      : {
          isPrimary: false,
        },
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.package.addMedia.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.package.updateMedia.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: MediaFormData) => {
    setIsSubmitting(true);

    if (isEditing && media) {
      updateMutation.mutate({
        id: media.id,
        data: {
          url: data.url,
          alt: data.alt || undefined,
          caption: data.caption || undefined,
          isPrimary: data.isPrimary,
        },
      });
    } else {
      createMutation.mutate({
        packageId,
        url: data.url,
        alt: data.alt,
        caption: data.caption,
        isPrimary: data.isPrimary,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Media' : 'Add Media'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the media information below.'
              : 'Add a new image to the package gallery.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="url">Image URL *</Label>
            <Input
              id="url"
              {...register('url', {
                required: 'Image URL is required',
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'Please enter a valid URL',
                }
              })}
              placeholder="https://example.com/image.jpg"
            />
            {errors.url && (
              <p className="text-sm text-red-500">{errors.url.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="alt">Alt Text</Label>
            <Input
              id="alt"
              {...register('alt')}
              placeholder="Describe the image for accessibility"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="caption">Caption</Label>
            <Input
              id="caption"
              {...register('caption')}
              placeholder="Optional caption for the image"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPrimary"
                {...register('isPrimary')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isPrimary" className="font-normal">
                Set as primary image
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              The primary image will be shown as the main thumbnail
            </p>
          </div>

          {/* Preview */}
          {watch('url') && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                <img
                  src={watch('url')}
                  alt={watch('alt') || 'Preview'}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* Error Messages */}
          {(createMutation.error || updateMutation.error) && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error?.message || updateMutation.error?.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Add Media'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
